import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useEffect, useId, useState } from 'react';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { Dropdown } from 'primereact/dropdown';
import { InputNumber } from 'primereact/inputnumber';
import { InputText } from 'primereact/inputtext';
import { DialogHeader } from '@/components/dialog/dialog-header';
import { useToastContext } from '@/layout/context/toast-context';
import { BREAK_REASON_OPTIONS, BREAK_REASON_OTHER } from '@/shared/constants/break-reasons';
export default function BreakRequestDialog({ visible, onHide, onConfirmBreak }) {
    const reasonTypeId = useId();
    const otherReasonId = useId();
    const minutesId = useId();
    const { showToast } = useToastContext();
    const [reasonType, setReasonType] = useState(null);
    const [otherReason, setOtherReason] = useState('');
    const [estimatedMinutes, setEstimatedMinutes] = useState(15);
    const [submitting, setSubmitting] = useState(false);
    const reset = useCallback(() => {
        setReasonType(null);
        setOtherReason('');
        setEstimatedMinutes(15);
        setSubmitting(false);
    }, []);
    useEffect(() => {
        if (!visible)
            reset();
    }, [visible, reset]);
    const handleHide = useCallback(() => {
        reset();
        onHide();
    }, [onHide, reset]);
    const handleReasonTypeChange = (value) => {
        setReasonType(value);
        if (value !== BREAK_REASON_OTHER) {
            setOtherReason('');
        }
    };
    const handleOk = async () => {
        if (!reasonType) {
            showToast({
                severity: 'warn',
                summary: 'Break reason required',
                detail: 'Please select a break reason.'
            });
            return;
        }
        let resolvedReason = reasonType;
        if (reasonType === BREAK_REASON_OTHER) {
            const trimmedOther = otherReason.trim();
            if (!trimmedOther) {
                showToast({
                    severity: 'warn',
                    summary: 'Reason required',
                    detail: 'Please describe your break reason.'
                });
                return;
            }
            resolvedReason = trimmedOther;
        }
        const estimatedBreakDuration = estimatedMinutes == null ? NaN : Math.trunc(Number(estimatedMinutes));
        if (!Number.isFinite(estimatedBreakDuration) || estimatedBreakDuration < 1) {
            showToast({
                severity: 'warn',
                summary: 'Estimated time required',
                detail: 'Please enter an estimated break duration of at least 1 minute.'
            });
            return;
        }
        const reasonSummary = `${resolvedReason} · ${estimatedBreakDuration} min planned`;
        if (onConfirmBreak) {
            setSubmitting(true);
            try {
                await onConfirmBreak({
                    breakReason: resolvedReason,
                    estimatedBreakDuration,
                    reasonSummary,
                });
                showToast({
                    severity: 'success',
                    summary: 'On break — not receiving queue calls',
                    detail: `${resolvedReason} (${estimatedBreakDuration} min planned)`
                });
                handleHide();
            }
            catch (e) {
                const detail = e instanceof Error ? e.message : 'Could not start break.';
                showToast({
                    severity: 'error',
                    summary: 'Could not pause for break',
                    detail,
                });
            }
            finally {
                setSubmitting(false);
            }
            return;
        }
        showToast({
            severity: 'success',
            summary: 'Break request submitted',
            detail: `${resolvedReason} · ${estimatedBreakDuration} min`
        });
        handleHide();
    };
    return (_jsx(Dialog, { header: _jsx(DialogHeader, { icon: "pi pi-clock", label: "Apply for break" }), visible: visible, modal: true, draggable: false, resizable: false, dismissableMask: true, style: { width: '96vw', maxWidth: '28rem' }, onHide: handleHide, footer: _jsxs("div", { className: "flex justify-content-end gap-2", children: [_jsx(Button, { type: "button", label: "Cancel", severity: "secondary", text: true, disabled: submitting, onClick: handleHide }), _jsx(Button, { type: "button", label: "Apply for break", loading: submitting, disabled: submitting, onClick: () => void handleOk() })] }), children: _jsxs("div", { className: "flex flex-column gap-3", children: [_jsxs("div", { className: "field mb-0", children: [_jsx("label", { htmlFor: reasonTypeId, className: "block text-900 font-medium mb-2", children: "Break reason" }), _jsx(Dropdown, { id: reasonTypeId, value: reasonType, onChange: (e) => handleReasonTypeChange(e.value ?? null), options: BREAK_REASON_OPTIONS, optionLabel: "label", optionValue: "value", placeholder: "Select break reason", className: "w-full", filter: true, showClear: true, disabled: submitting })] }), reasonType === BREAK_REASON_OTHER ? (_jsxs("div", { className: "field mb-0", children: [_jsx("label", { htmlFor: otherReasonId, className: "block text-900 font-medium mb-2", children: "Describe reason" }), _jsx(InputText, { id: otherReasonId, value: otherReason, onChange: (e) => setOtherReason(e.target.value), className: "w-full", placeholder: "Enter your reason\u2026", disabled: submitting })] })) : null, _jsxs("div", { className: "field mb-0", children: [_jsx("label", { htmlFor: minutesId, className: "block text-900 font-medium mb-2", children: "Estimated time (minutes)" }), _jsx(InputNumber, { id: minutesId, value: estimatedMinutes, onValueChange: (e) => setEstimatedMinutes(e.value ?? null), min: 1, max: 480, showButtons: true, className: "w-full", inputClassName: "w-full", disabled: submitting })] })] }) }));
}
